import { motion, useReducedMotion } from "motion/react";
import { manifestoCopy } from "../../content";
import { WaveRule } from "../art/WaveRule";
import { AutterAnchor, CaptainPatchImage } from "../brand/AutterBrand";

/** Full-width type statement: the thesis of Same Boat, set like dock signage. */
export function Manifesto() {
  const reduce = useReducedMotion();
  const rise = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.65, delay, ease: [0.16, 1, 0.3, 1] as const },
  });

  return (
    <section className="relative overflow-hidden bg-paper-deep py-24 md:py-36">
      <AutterAnchor className="pointer-events-none absolute -right-32 top-1/2 w-[30rem] -translate-y-1/2 -rotate-12 opacity-[0.055]" />
      <div className="relative mx-auto grid w-full max-w-[1400px] gap-14 px-5 md:px-10 lg:grid-cols-[1fr_auto] lg:items-end">
        {/* Both lines live inside the h2 unbroken, so the heading reads as the
            full two-sentence statement without an sr-only duplicate. */}
        <h2 className="display-type text-[clamp(2rem,4.6vw,4.2rem)]">
          <motion.span
            {...rise(0)}
            className="block max-w-[26ch] text-balance text-ink-soft"
          >
            {manifestoCopy.lineOne}
          </motion.span>
          <motion.span {...rise(0.06)} aria-hidden className="my-6 block md:my-8">
            <WaveRule className="text-signal" />
          </motion.span>
          <motion.span
            {...rise(0.12)}
            className="block max-w-[26ch] text-balance text-ink"
          >
            {manifestoCopy.lineTwo}
          </motion.span>
        </h2>

        <motion.p {...rise(0.18)} className="mt-10 font-mono text-sm text-muted md:mt-12">
          {manifestoCopy.small}
        </motion.p>

        <motion.div {...rise(0.18)} className="hidden items-end gap-3 lg:flex" aria-hidden>
          <div className="mb-16 w-56 rotate-[-2deg] rounded-2xl rounded-br-sm border border-ink/15 bg-foam px-5 py-4 shadow-card">
            <p className="font-mono text-[9px] tracking-[0.12em] text-signal-deep uppercase">The captain's rule</p>
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">Different company. Different problem. Same weather.</p>
          </div>
          <CaptainPatchImage pose="point" className="h-44 w-44 drop-shadow-xl" />
        </motion.div>
      </div>
    </section>
  );
}
